"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import HeroLaw from "@/components/start/HeroLaw"
import BenefitCard from "@/components/start/LawFirmRegistration/BenefitCard"
import { useGoogleAuth } from "@/hooks/Auth/useGoogleAuth"
import OwnerRegistrationForm from "@/components/start/LawFirmRegistration/OwnerRegistrationForm"
import BasicLawFirmForm from "@/components/start/LawFirmRegistration/BasicLawFirmForm"
import AdditionalLawFirmModal from "@/components/start/LawFirmRegistration/AdditionalLawFirmModal"
import Footer from "@/components/start/Footer"
import Swal from "sweetalert2"

// service
import { registerBufete } from "@/services/Lawyers/registerBufete"

function LawFirmRegistration() {
  // Estado inicial con objetos separados para owner y org (bufete)
  const [formData, setFormData] = useState({
    owner: {
      username: "",
      about_me: "",
      email: "",
      password: "",
      user_type: "Lawyer", // Valor por defecto
      imageUrl: "",
      google_login: false,
    },
    org: {
      name: "",
      email: "",
      phone: "",
      website: "",
      about_us: "",
      address: "",
      postal_code: "",
      city: "",
      province: "",
      numColegiado: "",
    },
  })

  // Estado para controlar el flujo del formulario
  const [currentStep, setCurrentStep] = useState(1) // 1: Owner, 2: Basic Law Firm, 3: Additional Law Firm
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Función para manejar cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value } = e.target

    // Determinar a qué objeto pertenece el campo (owner u org)
    if (name.startsWith("owner.")) {
      const fieldName = name.replace("owner.", "")
      setFormData((prev) => ({
        ...prev,
        owner: {
          ...prev.owner,
          [fieldName]: value,
        },
      }))
    } else if (name.startsWith("org.")) {
      const fieldName = name.replace("org.", "")

      // Manejar campos anidados (ubication)
      if (fieldName.includes(".")) {
        const [parent, child] = fieldName.split(".")
        setFormData((prev) => ({
          ...prev,
          org: {
            ...prev.org,
            [parent]: {
              ...prev.org[parent],
              [child]: value,
            },
          },
        }))
      } else {
        // Campos normales de org
        setFormData((prev) => ({
          ...prev,
          org: {
            ...prev.org,
            [fieldName]: value,
          },
        }))
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Enviar los datos estructurados (owner y org) al servicio registerBufete
      await registerBufete(formData)

      setShowModal(false) // Cerrar el modal automáticamente

      // Mostrar alerta de éxito
      Swal.fire({
        title: "¡Registro exitoso!",
        text: "Tu bufete ha sido registrado correctamente. Pronto nos pondremos en contacto contigo.",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "var(--navy-blue)",
      })

      // Resetear el formulario
      setFormData({
        owner: {
          username: "",
          about_me: "",
          email: "",
          password: "",
          user_type: "Lawyer",
          imageUrl: "",
          google_login: false,
        },
        org: {
          name: "",
          email: "",
          phone: "",
          website: "",
          about_us: "",
          address: "",
          postal_code: "",
          city: "",
          province: "",
          numColegiado: "",
        },
      })

      // Volver al primer paso
      setCurrentStep(1)
    } catch (error) {
      // Mostrar alerta de error
      Swal.fire({
        title: "Error",
        text: error.message || "Ha ocurrido un error al registrar tu bufete",
        icon: "error",
        confirmButtonText: "Intentar de nuevo",
        confirmButtonColor: "var(--navy-blue)",
      })
      console.error("Error al registrar bufete:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConsultaClick = () => {
    const form = document.getElementById("registrationForm")
    form.scrollIntoView({ behavior: "smooth" })
  }

  // Función para avanzar al siguiente paso (de Owner a Basic Law Firm)
  const handleNextToLawFirm = (e) => {
    e.preventDefault()

    // Validar campos del propietario
    const { username, email, password } = formData.owner

    if (!username || !email || !password) {
      Swal.fire({
        title: "Campos incompletos",
        text: "Por favor completa todos los campos obligatorios",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "var(--navy-blue)",
      })
      return
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      Swal.fire({
        title: "Email inválido",
        text: "Por favor ingresa un correo electrónico válido",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "var(--navy-blue)",
      })
      return
    }

    // Validar longitud de contraseña
    if (password.length < 6) {
      Swal.fire({
        title: "Contraseña débil",
        text: "La contraseña debe tener al menos 6 caracteres",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "var(--navy-blue)",
      })
      return
    }

    // Avanzar al siguiente paso
    setCurrentStep(2)
  }

  // Función para avanzar al modal de información adicional
  const handleNextToAdditional = (e) => {
    e.preventDefault()

    // Validar campos del bufete
    const { name, email, phone } = formData.org

    if (!name || !email || !phone) {
      Swal.fire({
        title: "Campos incompletos",
        text: "Por favor completa todos los campos obligatorios",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "var(--navy-blue)",
      })
      return
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      Swal.fire({
        title: "Email inválido",
        text: "Por favor ingresa un correo electrónico válido",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "var(--navy-blue)",
      })
      return
    }

    setShowModal(true)
  }

  // Función para volver al paso anterior
  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const { register, user, error } = useGoogleAuth("Lawyer")

  // Función para manejar el registro con Google
  const handleGoogleLogin = async () => {
    try {
      await register()
      if (user && !error) {
        // Actualizar el estado con los datos del usuario de Google
        setFormData((prev) => ({
          ...prev,
          owner: {
            ...prev.owner,
            username: user.name || prev.owner.username,
            email: user.email || prev.owner.email,
            imageUrl: user.photoURL || prev.owner.imageUrl,
            google_login: true,
          },
        }))

        Swal.fire({
          title: "¡Bienvenido!",
          text: `Has iniciado sesión como ${user.name}`,
          icon: "success",
          confirmButtonText: "Continuar",
          confirmButtonColor: "var(--navy-blue)",
        })

        // Avanzar al siguiente paso automáticamente
        setCurrentStep(2)
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "No se pudo iniciar sesión con Google",
        icon: "error",
        confirmButtonText: "Intentar de nuevo",
        confirmButtonColor: "var(--navy-blue)",
      })
    }
  }

  return (
    <>
      <HeroLaw
        titulo="Únete a Nuestra Red de Excelencia Legal"
        subtitulo="Registra tu bufete y expande tu alcance profesional"
        imagenSrc="https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
        imagenAlt="Oficina de abogados moderna"
        botonTexto="Comenzar Registro"
        onBotonClick={handleConsultaClick}
      />

      <motion.div
        className="container mx-auto px-4 py-16"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-12">
          <motion.h2
            className="text-3xl font-bold text-gray-800 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Registro de Bufete de Abogados
          </motion.h2>
          <motion.p
            className="text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Complete el formulario para registrar su bufete y comenzar a recibir consultas de clientes potenciales
          </motion.p>
        </div>

        <motion.section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start max-w-full mx-auto">
          <BenefitCard />

          {currentStep === 1 && (
            <OwnerRegistrationForm
              formData={formData.owner}
              handleChange={handleChange}
              handleNextStep={handleNextToLawFirm}
              login={handleGoogleLogin}
              user={user}
              error={error}
            />
          )}

          {currentStep === 2 && (
            <BasicLawFirmForm
              formData={formData.org}
              handleChange={handleChange}
              handleNextStep={handleNextToAdditional}
              handlePrevStep={handlePrevStep}
            />
          )}
        </motion.section>
      </motion.div>

      <AnimatePresence>
        {showModal && (
          <AdditionalLawFirmModal
            formData={formData.org}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            closeModal={() => setShowModal(false)}
            isSubmitting={isSubmitting}
          />
        )}
      </AnimatePresence>
      <Footer />
    </>
  )
}

export default LawFirmRegistration

